import { every, find, isEmpty } from 'lodash';

import { type ShiftType } from '@/components/ProjectSettings/ProjectShifts';
import {
  type ActivityInput,
  type AddressInput,
  type ClientRepInput,
  type ProjectCreate,
  type ProjectSettingsCreate,
} from '@/graphql/generated/graphql';

export const MIN_TITLE_LENGTH = 5;
export const MIN_PHONE_DIGITS = 10;

const EMAIL_REGEX = /\S+@\S+\.\S+/;

export const isValidEmail = (email: string | null | undefined) => EMAIL_REGEX.test(email ?? '');

export const isValidPhone = (phone: string | null | undefined) => Boolean(phone && phone.length >= MIN_PHONE_DIGITS);

export const isValidTitle = (title: string | null | undefined) => (title ?? '').length >= MIN_TITLE_LENGTH;

// Only the name is required; email and phone just need to be valid when provided
export const isClientRepComplete = (rep: ClientRepInput) =>
  Boolean(rep.name) && (!rep.email || isValidEmail(rep.email)) && (!rep.phone || isValidPhone(rep.phone));

export const isAddressComplete = (address: AddressInput) =>
  Boolean(address.street?.trim() && address.city?.trim() && address.state?.trim() && address.zip?.trim());

// endTime is non-nullable on ActivityInput, so the mutation rejects activities without it
export const isActivityComplete = (activity: ActivityInput) =>
  Boolean(activity.title && activity.startTime && activity.endTime);

export const parseProjectShifts = (settings: ProjectSettingsCreate[] | null | undefined): ShiftType[] => {
  const raw = find(settings ?? [], (setting) => setting.name === 'project-shifts')?.value;
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const canCreateProject = (project: ProjectCreate) => {
  const { title, code, clientId, clientReps, addresses, activities, projectUsers, settings } = project;

  const hasValidClientData = !clientId || every(clientReps ?? [], isClientRepComplete);
  const hasValidManagers = every(projectUsers ?? [], (manager) => !isEmpty(manager.title));
  const hasValidAddresses = every(addresses ?? [], isAddressComplete);
  const hasValidActivities = every(activities ?? [], isActivityComplete);
  const hasValidShifts = every(parseProjectShifts(settings), (shift) => shift.name !== '');

  return (
    isValidTitle(title) &&
    code !== '' &&
    hasValidClientData &&
    hasValidManagers &&
    hasValidAddresses &&
    hasValidActivities &&
    hasValidShifts
  );
};
